import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
  type Query,
} from "@tanstack/react-query";
import AxiosInstance from "../../../shared/utils/axios";
import { mergeStripPlotsIntoGroup } from "./splitStripPlots";
import { useAuth } from "../../../lib/auth/AuthContext";
import { getAuditFields } from "../../../shared/utils/auditFields";

/**
 * fetches the trial details for the given trialId
 * @param trialId
 * @returns trial details for the given trialId
 */
export const useTrial = (trialId: string | undefined): UseQueryResult<any> => {
  return useQuery({
    enabled: !!trialId,
    queryKey: ["trial", trialId],
    queryFn: async (): Promise<any> => {
      const response = await AxiosInstance.get(`/trial/${trialId as string}`);
      const loadedData = response.data;
      // Backend stores strips as N separate MultiPolygon plots; the app model
      // treats a strip design as ONE group. Merge them back on load so
      // rendering/colouring/labels/rates all see a single strip group.
      return {
        ...loadedData,
        trial_plots: mergeStripPlotsIntoGroup(
          loadedData?.trial_plots,
          loadedData?.strip_design?.is_strip === true
        ),
      };
    },
  });
};

export interface SaveTrialVariables {
  payload: any;
}

/**
 * creates a new trial with the given payload, stamped with the logged-in user
 * as `created_by` -- and invalidates the trials list insights cards on
 * success, since a new trial can shift their counts.
 */
export const useSaveTrial = (): UseMutationResult<
  any,
  unknown,
  SaveTrialVariables
> => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ payload }: SaveTrialVariables): Promise<any> => {
      const response = await AxiosInstance.post("/trial", {
        ...payload,
        ...getAuditFields("post", user?.id),
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["trialInsights"] });
    },
  });
};

export interface UpdateTrialVariables {
  trialId: string;
  payload: any;
  resetRatesDosage?: boolean;
}

interface RatesDosageResetTrial {
  id: string;
  protocol_id: string;
  field_ids?: string[];
  completion?: boolean;
  standard_field_properties?: Record<string, unknown> | null;
  plot_property_assignments?: {
    seed_product_assignments?: unknown[] | null;
    biological_treatment_assignments?: unknown[] | null;
  } | null;
  strip_design?: { is_strip?: boolean };
  trial_plots: Array<{
    plot?: {
      rows?: number;
      columns?: number;
      row_size?: number;
      column_size?: number;
      totalStrips?: number;
      isStripPlot?: boolean;
      is_strip?: boolean;
      geojson?: unknown;
    };
    properties?: {
      seeds?: { rates_and_dosages?: unknown[] } | null;
      fertilisers?: { rates_and_dosages?: unknown[] } | null;
      biologicals?: { treatments?: unknown[][] } | null;
    };
  }>;
}

const hasAssignments = (value: unknown): boolean => {
  if (value == null) return false;
  return !Array.isArray(value) || value.length > 0;
};

const areStandardPropertiesCleared = (
  standardProperties: RatesDosageResetTrial["standard_field_properties"]
): boolean => {
  const properties = standardProperties ?? {};
  const emptyValues: Record<string, unknown> = {
    standard_seed_product_id: null,
    standard_product: "",
    standard_seed_rate: 0,
    standard_fertilisers: [],
    standard_biological_application_settings: [],
    standard_tank_mix_rates: [],
  };

  return Object.entries(emptyValues).every(([key, emptyValue]) => {
    const value = properties[key];
    if (Array.isArray(emptyValue)) {
      return value == null || (Array.isArray(value) && value.length === 0);
    }
    return value == null || value === emptyValue;
  });
};

const getPlotDesign = (
  trialPlots: RatesDosageResetTrial["trial_plots"]
): unknown[] =>
  trialPlots.map(({ plot }) => ({
    rows: plot?.rows,
    columns: plot?.columns,
    row_size: plot?.row_size,
    column_size: plot?.column_size,
    totalStrips: plot?.totalStrips,
    isStripPlot: plot?.isStripPlot,
    is_strip: plot?.is_strip,
    geojson: plot?.geojson,
  }));

const normalizeFieldIds = (fieldIds: unknown): string[] =>
  Array.isArray(fieldIds)
    ? fieldIds
        .filter(
          (fieldId): fieldId is string =>
            typeof fieldId === "string" && fieldId.length > 0
        )
        .sort()
    : [];

const sameFieldIds = (left: unknown, right: unknown): boolean =>
  JSON.stringify(normalizeFieldIds(left)) ===
  JSON.stringify(normalizeFieldIds(right));

/**
 * patches the trial with the given trialId -- stamped with the logged-in user
 * as `updated_by` -- and invalidates its cached `useTrial` query plus the
 * trials list insights cards on success, so any component reading that trial
 * or its counts picks up the freshly saved data. Verified Rates & Dosage resets
 * also discard this trial's assignment and scoped trial-query caches before
 * the standalone route remounts.
 */
export const useUpdateTrial = (): UseMutationResult<
  any,
  unknown,
  UpdateTrialVariables
> => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      trialId,
      payload,
      resetRatesDosage,
    }: UpdateTrialVariables): Promise<any> => {
      const response = await AxiosInstance.patch(`/trial/${trialId}`, {
        ...payload,
        ...getAuditFields("patch", user?.id),
      });
      if (resetRatesDosage) {
        const { data: savedTrial } =
          await AxiosInstance.get<RatesDosageResetTrial>(`/trial/${trialId}`);
        if (
          savedTrial?.id !== trialId ||
          savedTrial.protocol_id !== payload.protocol_id ||
          !sameFieldIds(savedTrial.field_ids, payload.field_ids) ||
          savedTrial.completion !== false ||
          !areStandardPropertiesCleared(savedTrial.standard_field_properties) ||
          hasAssignments(
            savedTrial.plot_property_assignments?.seed_product_assignments
          ) ||
          hasAssignments(
            savedTrial.plot_property_assignments
              ?.biological_treatment_assignments
          ) ||
          !Array.isArray(savedTrial.trial_plots) ||
          savedTrial.trial_plots.length !== payload.trial_plots.length ||
          JSON.stringify(getPlotDesign(savedTrial.trial_plots)) !==
            JSON.stringify(getPlotDesign(payload.trial_plots)) ||
          savedTrial.trial_plots.some(({ properties }) =>
            Boolean(
              (properties?.seeds?.rates_and_dosages?.length ?? 0) > 0 ||
                (properties?.fertilisers?.rates_and_dosages?.length ?? 0) > 0 ||
                properties?.biologicals?.treatments?.some((application) =>
                  application.some((cell) => cell != null)
                )
            )
          )
        ) {
          throw new Error(
            "The trial changed, but the saved Rates & Dosage assignments were not cleared or the reset could not be verified. Retry saving Basic Info."
          );
        }
        return savedTrial;
      }
      return response.data;
    },
    onSuccess: async (data, variables) => {
      if (variables.resetRatesDosage) {
        const trialKey = ["trial", variables.trialId];
        await queryClient.cancelQueries({ queryKey: trialKey });
        queryClient.setQueryData(trialKey, {
          ...data,
          trial_plots: mergeStripPlotsIntoGroup(
            data.trial_plots,
            data.strip_design?.is_strip === true
          ),
        });
        queryClient.removeQueries({
          queryKey: trialKey,
          predicate: (query: Query) =>
            query.queryKey[1] === variables.trialId &&
            query.queryKey.length > trialKey.length,
        });
        await Promise.all(
          [
            "seedAssignmentsTrial",
            "stripSeedAssignmentsTrial",
            "seedAutoAssignments",
          ].map(async (key) => {
            const filters = {
              queryKey: [key],
              predicate: (query: Query) =>
                query.queryKey[3] === variables.trialId,
            };
            await queryClient.cancelQueries(filters);
            queryClient.removeQueries(filters);
          })
        );
      } else {
        void queryClient.invalidateQueries({
          queryKey: ["trial", variables.trialId],
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["trialInsights"] });
    },
  });
};
